import { generateW8BENXML } from "./utils/generateW8BENXML";
import { generateW8EXPXML } from "./utils/generateW8EXPXML";
import { generateW9XML } from "./utils/generateW9XML";
import { pythonBaseUrl } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { generateW8ECIXML } from "./utils/generateW8ECI";
import { generateW8BENEXML } from "./utils/generateW8BENEXML";
import { generateW8IMYXML } from "./utils/generateW8IMYXML";
import { generateForm8233XML } from "./utils/generateForm8233XML";
import { generateSelfCertIndividualXML } from "./utils/generateSelfCertIndividualXML";
import { generateSelfCertEntityXML } from "./utils/generateSelfCertEntityXML";

interface FormData {
  [key: string]: string | boolean;
}

interface W8BENApiHandlerProps {
  formData: FormData;
  formName?: string; // Optional form name
  formType?: string; // Optional form type
  onSubmitSuccess?: (response: any) => void;
  onSubmitError?: (error: any) => void;
}
const W8BENApiHandler = async ({
  formData,
  formName,
  formType,
  onSubmitSuccess,
  onSubmitError,
}: W8BENApiHandlerProps) => {
  if (!formData || Object.keys(formData).length === 0) return;

  const getValue = (key: string, defaultValue: string = "") => formData[key] || defaultValue;

  try {
    // Clean up form type for consistent comparison
    const normalizedFormType = formType?.toLowerCase().trim() || "";

    // Determine XML generation based on form type
    let xml: string;
    switch (true) {
      case normalizedFormType.includes("w-8ben-e") || normalizedFormType.includes("w8bene"):
        xml = await generateW8BENEXML(formData);
        break;
      case normalizedFormType.includes("w-8ben") || normalizedFormType.includes("w8ben"):
        xml = await generateW8BENXML(formData);
        break;
      case normalizedFormType.includes("w-8exp") || normalizedFormType.includes("w8exp"):
        xml = await generateW8EXPXML(formData);
        break;
      case normalizedFormType.includes("w-8eci") || normalizedFormType.includes("w8eci"):
        xml = await generateW8ECIXML(formData);
        break;
      case normalizedFormType.includes("w-9") || normalizedFormType.includes("w9"):
        xml = await generateW9XML(formData);
        break;
      case normalizedFormType.includes("w-8imy") || normalizedFormType.includes("w8imy"):
        xml = await generateW8IMYXML(formData);
        break;
      case normalizedFormType.includes("8233") || normalizedFormType.includes("8233"):
        xml = await generateForm8233XML(formData);
        break;
      case normalizedFormType.includes("selfcert-ind") ||
        normalizedFormType.includes("self-cert individual"):
        xml = await generateSelfCertIndividualXML(formData);
        break;
      case normalizedFormType.includes("selfcert-ent") ||
        normalizedFormType.includes("self-cert entity"):
        xml = await generateSelfCertEntityXML(formData);
        break;
      default:
        // Fallback to W8BEN if no specific type is identified
        xml = await generateW8BENXML(formData);
    }

    // Create FormData and add the XML content and metadata
    const formDataToSend = new FormData();
    // Add XML directly as a file (same as uploading any file)
    const xmlFile = new File([xml], "form-data.xml", { type: "application/xml" });
    formDataToSend.append("xmlFile", xmlFile);

    formDataToSend.append("UniqueIdentifier", `${getValue("UniqueIdentifier")}`);
    formDataToSend.append("FormName", formName || formType || "Unknown");
    formDataToSend.append("FormType", formType || "Unknown");
    formDataToSend.append("OrganizationId", getUserIdFromLocalStorage().organizationId);
    formDataToSend.append("UserId", getUserIdFromLocalStorage().userId);

    // Make the fetch POST request
    const response = await fetch(`${pythonBaseUrl}/Form/SubmitForm`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`, // ✅ Only Authorization header
      },
      body: formDataToSend,
    });

    // Check if response is successful
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorData}`);
    }

    // Parse response data
    const responseData = await response.json();

    onSubmitSuccess?.(responseData);
    return responseData;
  } catch (error: any) {
    console.error("Error submitting form:", error);
    console.error("Error details:", error.message);
    onSubmitError?.(error.message);
    throw error;
  }
};

export default W8BENApiHandler;
